import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { apiError } from "../lib/api";
import { Atom, LogIn, UserPlus, GraduationCap, School } from "lucide-react";

export default function Login() {
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("student");
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      if (mode === "login") await login(email, password);
      else await register(email, password, name, role);
      navigate(role === "teacher" && mode === "register" ? "/teacher" : "/dashboard");
    } catch (ex) {
      setErr(apiError(ex));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-md space-y-8" data-testid="auth-root">
      <div className="text-center">
        <div className="mx-auto grid h-14 w-14 place-items-center rounded-md border border-cyan-400/40 bg-cyan-500/10 text-cyan-400">
          <Atom size={22} />
        </div>
        <h1 className="font-display mt-6 text-3xl font-bold">
          {mode === "login" ? "Welcome back" : "Create your account"}
        </h1>
        <p className="mt-2 text-sm text-slate-400">
          {mode === "login" ? "Sign in to keep your streak going." : "Track your progress. Join in seconds."}
        </p>
      </div>

      <form onSubmit={submit} className="space-y-4 rounded-md border border-white/10 bg-black/40 p-6" data-testid="auth-form">
        {mode === "register" && (
          <>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
              required
              data-testid="auth-name"
              className="w-full rounded-md border border-white/10 bg-black/60 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-500 focus:border-cyan-400/60 focus:outline-none"
            />
            <div className="grid grid-cols-2 gap-2">
              {[
                { v: "student", label: "Student", Icon: GraduationCap },
                { v: "teacher", label: "Teacher", Icon: School },
              ].map(({ v, label, Icon }) => (
                <button
                  key={v}
                  type="button"
                  data-testid={`role-${v}`}
                  onClick={() => setRole(v)}
                  className={`flex items-center justify-center gap-2 rounded-md border px-3 py-3 text-sm font-medium ${
                    role === v ? "border-cyan-400/60 bg-cyan-500/10 text-cyan-200" : "border-white/10 bg-white/5 text-slate-400"
                  }`}
                >
                  <Icon size={14} /> {label}
                </button>
              ))}
            </div>
          </>
        )}
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="email@school.edu"
          required
          data-testid="auth-email"
          className="w-full rounded-md border border-white/10 bg-black/60 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-500 focus:border-cyan-400/60 focus:outline-none"
        />
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Password (min 6 characters)"
          required
          minLength={6}
          data-testid="auth-password"
          className="w-full rounded-md border border-white/10 bg-black/60 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-500 focus:border-cyan-400/60 focus:outline-none"
        />
        {err && <div className="rounded-md border border-red-400/40 bg-red-500/10 p-3 text-xs text-red-200" data-testid="auth-error">{err}</div>}
        <button
          type="submit"
          disabled={busy}
          data-testid="auth-submit"
          className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-cyan-500 px-4 py-3 text-sm font-semibold text-black hover:bg-cyan-400 disabled:opacity-50"
        >
          {mode === "login" ? <><LogIn size={14} /> Sign in</> : <><UserPlus size={14} /> Create account</>}
        </button>
      </form>

      <div className="text-center text-sm text-slate-400">
        {mode === "login" ? "New to ChemLab?" : "Already have an account?"}{" "}
        <button
          type="button"
          onClick={() => { setMode(mode === "login" ? "register" : "login"); setErr(null); }}
          data-testid="auth-toggle"
          className="text-cyan-300 hover:underline"
        >
          {mode === "login" ? "Create one" : "Sign in"}
        </button>
        <div className="mt-4">
          <Link to="/" className="text-xs text-slate-500 hover:text-slate-300">← Back to home</Link>
        </div>
      </div>
    </div>
  );
}
